import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

import { EntityType } from '../_enums/entity-type.enum';
import { ProposalState } from '../_enums/proposal.enums';
import { buildSearchParams } from '../_helpers/utils';
import { APIResponse, BasicResponse } from '../_types/api-responses.types';
import {
  CancellationLetter,
  InsuranceMetadatas,
  Lead,
  SendGroupeMutuelLeadRequest,
  UpdateLeadStatusRequest,
} from '../_types/lead.types';
import {
  AcceptOfferRequest,
  AllProposalsResponse,
  AllTasksRequest,
  AllTasksResponse,
  CreateLeadResponse,
  ParseOfferPdfRequest,
  ParseOfferPdfResponse,
  ProposalDetails,
  ProposalFetchRequest,
  SendProposalsRequest,
  UpdateCentralisationStatusRequest,
  UpdateLeadMetadataRequest,
  UpdatePolicyRequest,
  UpdatePolicyResponse,
  UpdatePolicyStatusRequest,
  UpsertLeadRequest,
} from '../_types/proposals.type';

@Injectable({
  providedIn: 'root',
})
export class ProposalService {
  _headers: any;

  httpOptions: any;

  _headersWithToken: any;

  _baseURL: string;

  _CommunicatorbaseURL: string;

  _user: any = {};

  deviceInfo: any;

  _headersOTP: any;

  _FeebaseURL: any;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly deviceService: DeviceDetectorService
  ) {
    this.deviceInfo = this.deviceService.getDeviceInfo();
    const devicedetail: any = {
      platform: 'Website',
      browser: this.deviceInfo.browser,
      browserVersion: this.deviceInfo.browser_version,
      osVersion: this.deviceInfo.os_version,
      deviceId: this.deviceInfo.device + this.deviceInfo.deviceType,
      appVersion: '',
      ipAddress: '',
      macAddress: '',
    };
    this._headers = new HttpHeaders()
      .set('content-type', 'application/json')
      .set('User_Agent', JSON.stringify(devicedetail));
    this._headersOTP = new HttpHeaders()
      .set('content-type', 'application/json')
      .set('data-hash', 'test')
      .set('device-id', 'test')
      .set('User_Agent', JSON.stringify(devicedetail));

    this._baseURL = environment.INSURANCEAPIURL;
    this._FeebaseURL = environment.FEEAPIENDPOINT;
    this._CommunicatorbaseURL = environment.COMMUNICATORAPIURL;

    if (this.isUserLoggedIn()) {
      this.getCurrentUser();

      this._headersWithToken = new HttpHeaders()
        .set('content-type', 'application/json')
        .set('Access-Token', this._user.token)
        .set('User_Agent', JSON.stringify(devicedetail));
    }
  }

  get headersWithToken(): HttpHeaders {
    return this._headers.set('Access-Token', this.getCurrentUser().token);
  }

  get fileUploadHeaders(): HttpHeaders {
    return new HttpHeaders().set('Access-Token', this.getCurrentUser().token);
  }

  isUserLoggedIn(): boolean {
    const data = localStorage.getItem('user');
    if (data != null) {
      return true;
    }
    return false;
  }

  getCurrentUser(): any {
    const data: any = localStorage.getItem('user');
    if (data != null) {
      this._user = JSON.parse(data);
    }
    return this._user;
  }

  _addProposal(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}add/proposal`, obj, {
      headers: this.headersWithToken,
    });
  }

  _updateProposal(obj: any): Observable<any> {
    return this.httpClient.put(`${this._baseURL}update/proposal`, obj, {
      headers: this.headersWithToken,
    });
  }

  _getPartnerProposalList(
    obj: ProposalFetchRequest,
    fetchDocuments: boolean = false
  ): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}get/proposals`,
      { ...obj, fetchDocuments },
      {
        headers: this.headersWithToken,
      }
    );
  }

  _getPartnerProposalDetails(
    obj: any
  ): Observable<APIResponse<ProposalDetails>> {
    return this.httpClient.post<APIResponse<ProposalDetails>>(
      `${this._baseURL}get/proposal`,
      obj,
      {
        headers: this.headersWithToken,
      }
    );
  }

  _validatePolicyId(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}validate/policy-id`, obj, {
      headers: this.headersWithToken,
    });
  }

  _validateOfferId(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}validate/offer-id`, obj, {
      headers: this.headersWithToken,
    });
  }

  _getStaticData(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}get/static/data`, obj, {
      headers: this.headersWithToken,
    });
  }

  _getPartnerCustomerLeadList(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}get/leads`, obj, {
      headers: this.headersWithToken,
    });
  }

  _getCustomerLeadList(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}get/user/leads`, obj, {
      headers: this.headersWithToken,
    });
  }

  getLeadDetails(obj: any): Observable<APIResponse<Lead>> {
    return this.httpClient.post<APIResponse<Lead>>(
      `${this._baseURL}get/lead/details`,
      { ...obj, entityType: EntityType.ADMIN_ID, entityId: this._user.adminId },
      {
        headers: this.headersWithToken,
      }
    );
  }

  getRelatedLeads(
    leadId: string
  ): Observable<APIResponse<{ leadsList: Lead[] }>> {
    return this.httpClient.get<APIResponse<{ leadsList: Lead[] }>>(
      `${this._baseURL}get/lead/related/${leadId}`,
      {
        headers: this.headersWithToken,
      }
    );
  }

  getLeadDetailsByFlowId(obj: {
    flowId: string;
  }): Observable<APIResponse<Lead>> {
    return this.httpClient.post<APIResponse<Lead>>(
      `${this._baseURL}get/lead/details/flow-id`,
      { ...obj, entityType: EntityType.ADMIN_ID, entityId: this._user.adminId },
      {
        headers: this.headersWithToken,
      }
    );
  }

  _getPartnerLeadProposalList(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}get/lead/proposals`, obj, {
      headers: this.headersWithToken,
    });
  }

  _updateProposalState(obj: {
    proposalId: string;
    proposalState: ProposalState;
  }): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this._baseURL}update/proposal/state`,
      obj,
      {
        headers: this.headersWithToken,
      }
    );
  }

  _deleteSavedLeadProposal(proposalIds: string[]): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}delete/proposal`,
      { proposalIds },
      {
        headers: this.headersWithToken,
      }
    );
  }

  _getProposalSubmissionList(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}get/submissions`, obj, {
      headers: this.headersWithToken,
    });
  }

  _sendProposal(obj: SendProposalsRequest): Observable<any> {
    return this.httpClient.post(`${this._baseURL}send/proposal`, obj, {
      headers: this.headersWithToken,
    });
  }

  _addPolicyId(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}add/policy-id`, obj, {
      headers: this.headersWithToken,
    });
  }

  _getCustomerContractList(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}get/user/proposal`, obj, {
      headers: this.headersWithToken,
    });
  }

  _getOverallStatisticsList(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}get/overall/statistics`, obj, {
      headers: this.headersWithToken,
    });
  }

  _getCustomerStatisticsList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}get/customers/statistics`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  _getAgentStatisticsList(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}get/agents/statistics`, obj, {
      headers: this.headersWithToken,
    });
  }

  _getAnalysisList(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}get/analysis`, obj, {
      headers: this.headersWithToken,
    });
  }

  download(url: string): Observable<Blob> {
    this._headersWithToken = new HttpHeaders()
      .set('Access-Control-Allow-Origin', '*')
      .set('Access-Control-Allow-Methods', 'GET,POST,OPTIONS,DELETE,PUT');
    return this.httpClient.get(url, {
      responseType: 'blob',
    });
  }

  _getDashboardInfo(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}get/partner/dashboard-info`,
      obj,
      { headers: this.headersWithToken }
    );
  }

  _getExpiredContractList(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}get/expired/proposals`, obj, {
      headers: this.headersWithToken,
    });
  }

  assignLeadtoPartner(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}assign/partner-agent`, obj, {
      headers: this.headersWithToken,
    });
  }

  updateLeadStatus(
    obj: UpdateLeadStatusRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this._baseURL}update/lead/status`,
      obj,
      {
        headers: this.headersWithToken,
      }
    );
  }

  createLead(obj: UpsertLeadRequest) {
    return this.httpClient.post<APIResponse<CreateLeadResponse>>(
      `${this._baseURL}request/proposal`,
      obj,
      {
        headers: this.headersWithToken,
      }
    );
  }

  updateLeadMetadata(leadId: string, metadata: InsuranceMetadatas) {
    const body: UpdateLeadMetadataRequest = {
      leadId,
      metadata,
      entityId: this._user.adminId,
      entityType: EntityType.ADMIN_ID,
    };
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this._baseURL}update/lead/metadata`,
      body,
      {
        headers: this.headersWithToken,
      }
    );
  }

  updatePolicyStatus(request: UpdatePolicyStatusRequest) {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this._baseURL}update/policy-status`,
      request,
      { headers: this.headersWithToken }
    );
  }

  updatePolicy(request: UpdatePolicyRequest) {
    return this.httpClient.put<APIResponse<UpdatePolicyResponse>>(
      `${this._baseURL}update/policy`,
      request,
      { headers: this.headersWithToken }
    );
  }

  initProposalCommission(proposalId: string) {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}proposal/commission/init`,
      { proposalId },
      { headers: this.headersWithToken }
    );
  }

  patchLead(request: UpsertLeadRequest) {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}patch/lead`,
      request,
      { headers: this.headersWithToken }
    );
  }

  updateCentralisationTrackingStatus(
    request: UpdateCentralisationStatusRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}update/centralisation/status`,
      request,
      { headers: this.headersWithToken }
    );
  }

  deleteLead(leadId: string): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}delete/lead`,
      { leadId },
      {
        headers: this.headersWithToken,
      }
    );
  }

  getAllTasks(obj: AllTasksRequest): Observable<APIResponse<AllTasksResponse>> {
    return this.httpClient.post<APIResponse<AllTasksResponse>>(
      `${this._baseURL}get/tasks`,
      obj,
      {
        headers: this.headersWithToken,
      }
    );
  }

  getProposalDetails(
    proposalId: string
  ): Observable<APIResponse<ProposalDetails>> {
    return this.httpClient.post<APIResponse<ProposalDetails>>(
      `${this._baseURL}get/proposal/extended`,
      { proposalId },
      {
        headers: this.headersWithToken,
      }
    );
  }

  searchProposalsOfCustomer(
    query: string,
    familyMemberId?: string,
    limit?: number
  ): Observable<APIResponse<AllProposalsResponse>> {
    return this.httpClient.get<APIResponse<AllProposalsResponse>>(
      `${this._baseURL}get/proposals/search?${buildSearchParams({ query: query ?? '', familyMemberId, limit })}`,
      { headers: this.headersWithToken }
    );
  }

  generateCancellationLetter(
    leadId: string,
    letterInfo: CancellationLetter
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<APIResponse<BasicResponse>>>(
      `${this._baseURL}lead/cancellation-letter/generate`,
      { leadId, letterInfo },
      {
        headers: new HttpHeaders().set(
          'Access-Token',
          this.getCurrentUser().token
        ),
      }
    );
  }

  parseOfferPdf(
    request: ParseOfferPdfRequest
  ): Observable<APIResponse<ParseOfferPdfResponse>> {
    const form = new FormData();
    form.append('doc', request.doc);
    form.append('insuranceType', request.insuranceType);

    return this.httpClient.post<APIResponse<ParseOfferPdfResponse>>(
      `${this._baseURL}offer/parse`,
      form,
      {
        headers: this.fileUploadHeaders,
      }
    );
  }

  /**
   * Appends one PDF, typed POLICY, to an accepted offer or a policy
   * (OFFER_ACCEPTED, SIGNED_DOCS or POLICY_CREATED); the flow's history logs
   * it. The answer carries the new document's id, which the caller does not read.
   */
  addPolicyDocument(
    proposalId: string,
    file: File
  ): Observable<APIResponse<number>> {
    const form = new FormData();
    form.append('file', file, file.name);

    return this.httpClient.post<APIResponse<number>>(
      `${this._baseURL}proposal/${proposalId}/documents`,
      form,
      { headers: this.fileUploadHeaders }
    );
  }

  acceptOffer(
    request: AcceptOfferRequest
  ): Observable<APIResponse<BasicResponse>> {
    const form = new FormData();
    form.append('proposalId', request.proposalId);
    form.append(
      'signatureDate',
      request.signatureDate.toISOString()?.split('T')?.[0]
    );
    form.append('offerDocument', request.offerDocument);

    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}accept/offer`,
      form,
      {
        headers: this.fileUploadHeaders,
      }
    );
  }

  sendGroupeMutuelLead(
    request: SendGroupeMutuelLeadRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}send-groupe-mutuel-offer`,
      request,
      {
        headers: this.headersWithToken,
      }
    );
  }
}
